import React from "react";

import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

const videos = [
  {
    id: "DW_v4i-jcKS",
    title: "Vishwa Residency",
    description: "Ultra-luxury living in the heart of Ahmedabad"
  },
  {
    id: "DW1a_2dievb",
    title: "Luxury 4 BHK - Science Park",
    description: "Premium ready-to-move apartment near Science City"
  },
  {
    id: "DVBLGLXESiu",
    title: "Modern Living",
    description: "Experience elegance in every corner"
  },
  {
    id: "DU99H-5jE2A",
    title: "Skyline Views",
    description: "Breathtaking views from our latest projects"
  },
  {
    id: "DSXcYtsDf24",
    title: "Bungalow Life",
    description: "Exclusive detached villas for your family"
  },
  {
    id: "DPYr4gqEY-H",
    title: "Interior Design",
    description: "Curated spaces with modern aesthetics"
  },
  {
    id: "DM2DQs_owFz",
    title: "Prime Location",
    description: "Strategically located properties in Ahmedabad"
  },
  {
    id: "DMiNUM4Too0",
    title: "Dream Homes",
    description: "Find your perfect match with BluePeak"
  }
];

const AboutSection = () => {
  const [selectedVideo, setSelectedVideo] = React.useState(null);
  const modalRef = React.useRef(null);

  const openModal = (videoId) => {
    setSelectedVideo(videoId);
    document.body.style.overflow = 'hidden';

    const isMobile = window.matchMedia('(max-width: 768px)').matches;

    if (!isMobile) {
      setTimeout(() => {
        if (modalRef.current) {
          const requestFs = modalRef.current.requestFullscreen ||
            modalRef.current.webkitRequestFullscreen ||
            modalRef.current.mozRequestFullScreen ||
            modalRef.current.msRequestFullscreen;

          if (requestFs) {
            requestFs.call(modalRef.current).catch(err => {
              console.log(`Fullscreen API skipped or failed: ${err.message}`);
            });
          }
        }
      }, 100);
    }
  };

  const closeModal = React.useCallback(() => {
    const doc = document;
    const exitFs = doc.exitFullscreen || doc.webkitExitFullscreen || doc.mozCancelFullScreen || doc.msExitFullscreen;
    if (exitFs && (doc.fullscreenElement || doc.webkitFullscreenElement || doc.mozFullScreenElement || doc.msFullscreenElement)) {
      exitFs.call(doc).catch(() => { });
    }

    setSelectedVideo(null);
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
  }, []);

  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeModal();
    };

    const handleFullscreenChange = () => {
      if (!document.fullscreenElement &&
        !document.webkitFullscreenElement &&
        !document.mozFullScreenElement &&
        !document.msFullscreenElement &&
        selectedVideo) {
        closeModal();
      }
    };

    if (selectedVideo) {
      window.addEventListener('keydown', handleKeyDown);
      document.addEventListener('fullscreenchange', handleFullscreenChange);
      document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, [selectedVideo, closeModal]);

  return (
    <section className="w-full overflow-hidden py-12 md:py-16 lg:py-20 font-poppins bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Heading section */}
        <div className="text-center mb-10 sm:mb-14 max-w-3xl mx-auto">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold mb-4 text-gray-900">
            Discover Your Perfect Property
          </h2>
          <p className="text-gray-600 text-base sm:text-lg leading-relaxed">
            With years of experience in the real estate market, we specialize in helping you find properties that match your lifestyle and investment goals.
          </p>
        </div>

        {/* Videos Carousel */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-0">
          <Swiper
            modules={[Navigation, Pagination, Autoplay]}
            spaceBetween={20}
            slidesPerView={1.2}
            navigation={{
              nextEl: '.swiper-button-next-custom',
              prevEl: '.swiper-button-prev-custom',
            }}
            pagination={{
              el: '.swiper-pagination-custom',
              clickable: true,
              dynamicBullets: true
            }}
            autoplay={{
              delay: 4000,
              disableOnInteraction: false,
              pauseOnMouseEnter: true
            }}
            breakpoints={{
              480: {
                slidesPerView: 1.5,
                spaceBetween: 24,
              },
              768: {
                slidesPerView: 2.3,
              },
              1024: {
                slidesPerView: 3,
              },
              1280: {
                slidesPerView: 4,
              },
            }}
            className="relative"
          >
            {videos.map((video, index) => (
              <SwiperSlide key={index}>
                <div
                  className="relative w-full h-[500px] sm:h-[600px] rounded-2xl overflow-hidden shadow-lg bg-black cursor-pointer group"
                  onClick={() => openModal(video.id)}
                >
                  <div className="absolute inset-0 w-full h-full">
                    <iframe
                      src={`https://www.instagram.com/reel/${video.id}/embed/captioned`}
                      className="w-full h-full border-none"
                      scrolling="no"
                      allowTransparency="true"
                      title={video.title}
                    ></iframe>
                  </div>

                  {/* Enhanced masks to hide Instagram UI elements */}
                  {/* Top mask - hides profile info */}
                  <div className="absolute top-0 left-0 right-0 h-[70px] bg-gradient-to-b from-black via-black to-transparent z-10 pointer-events-none"></div>

                  {/* Bottom mask - hides like/comment/share buttons */}
                  <div className="absolute bottom-0 left-0 right-0 h-[100px] bg-gradient-to-t from-black via-black to-transparent z-10 pointer-events-none"></div>

                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 z-20" />

                  {/* Play button overlay */}
                  <div className="absolute inset-0 flex items-center justify-center z-30">
                    <div className="bg-white/20 backdrop-blur-md p-4 rounded-full border border-white/30 opacity-0 group-hover:opacity-100 transform scale-75 group-hover:scale-100 transition-all duration-300">
                      <svg className="w-8 h-8 text-white fill-current" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>

          {/* Custom Pagination - Hidden on Desktop */}
          <div className="swiper-pagination-custom flex justify-center mt-8 md:hidden"></div>

          {/* Custom Navigation buttons */}
          <button className="swiper-button-prev-custom absolute left-0 top-1/2 -translate-y-12 -translate-x-4 md:-translate-x-12 z-20 bg-white/80 backdrop-blur-md p-3 rounded-full shadow-lg text-gray-800 hover:bg-blue-600 hover:text-white transition-all duration-300 hidden sm:flex items-center justify-center border border-gray-100 group">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button className="swiper-button-next-custom absolute right-0 top-1/2 -translate-y-12 translate-x-4 md:translate-x-12 z-20 bg-white/80 backdrop-blur-md p-3 rounded-full shadow-lg text-gray-800 hover:bg-blue-600 hover:text-white transition-all duration-300 hidden sm:flex items-center justify-center border border-gray-100 group">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Video Modal - Fully Immersive Fullscreen */}
        {selectedVideo && (
          <div
            ref={modalRef}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black animate-in fade-in zoom-in duration-300"
            onClick={closeModal}
          >
            {/* Close Button */}
            <button
              className="absolute top-4 right-4 md:top-8 md:right-8 z-[150] bg-black/40 hover:bg-red-600 text-white p-4 md:p-3 rounded-full transition-all duration-300 backdrop-blur-md border border-white/20 group focus:outline-none"
              onClick={(e) => {
                e.stopPropagation();
                closeModal();
              }}
              title="Close Video"
              aria-label="Close video"
            >
              <svg className="w-8 h-8 md:w-10 md:h-10 transform group-hover:rotate-90 transition-transform duration-300 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Video Container */}
            <div
              className="w-full h-full flex items-center justify-center overflow-hidden bg-black"
              onClick={e => e.stopPropagation()}
            >
              <div className="relative w-full h-full flex items-center justify-center">
                <iframe
                  src={`https://www.instagram.com/reel/${selectedVideo}/embed/captioned`}
                  title="Instagram Reel Player"
                  className="w-full h-full border-none bg-black"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                  allowFullScreen
                ></iframe>

                {/* Modal Masks - More aggressive */}
                <div className="absolute top-0 left-0 right-0 h-[80px] bg-black z-10 pointer-events-none"></div>
                <div className="absolute bottom-0 left-0 right-0 h-[120px] bg-black z-10 pointer-events-none"></div>
              </div>
            </div>

            {/* Exit hint */}
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/30 text-xs font-light tracking-widest uppercase pointer-events-none hidden sm:block">
              Click anywhere outside or press ESC to exit
            </div>
          </div>
        )}

        {/* Areas We Serve Section */}
        <div className="mt-20 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-2xl md:text-4xl font-bold text-gray-900 mb-3">Serving All Areas of Ahmedabad</h3>
            <div className="w-20 h-1 bg-blue-600 mx-auto mb-6"></div>
            <p className="text-gray-600 max-w-2xl mx-auto">
              We provide exceptional real estate services across Ahmedabad's most sought-after locations
            </p>
          </div>

          <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4 md:gap-5">
            {[
              'Science Park', 'Ambli', 'Sindhu Bhavan', 'Hebatpur',
              'Thaltej', 'Shela', 'Shilaj', 'Ognaj',
              'Science City', 'Gota', 'Vaishnodevi', 'Zundal',
              'Gandhinagar', 'GIFT City'
            ].map((area, index) => (
              <div
                key={index}
                className="group relative bg-white p-4 rounded-xl shadow-sm border border-gray-100 hover:border-blue-100 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-white opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative z-10 flex items-center justify-center h-full">
                  <span className="text-gray-800 font-medium text-center group-hover:text-blue-700 transition-colors">
                    {area}
                  </span>
                </div>
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-blue-400 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-gray-600 mb-6">
              And many more areas across Ahmedabad! Contact us for properties in your preferred location.
            </p>
            <a
              href="#contact"
              onClick={(e) => {
                e.preventDefault();
                const contactSection = document.getElementById('contact');
                if (contactSection) {
                  contactSection.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-full text-white bg-blue-600 hover:bg-blue-700 transition-colors duration-300 shadow-sm"
              >
              Contact Us
              <svg className="ml-2 -mr-1 w-4 h-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd"></path>
              </svg>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
